import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { inject } from '@app/providers/QAU.ContextProvider';
import { isCustomShapeFlow, isPrintInstructionsReadOnly, isMobileMode } from '@app/utils/utils';
import { setPrintInstructions, setPrintInstructionsExpanded } from '@app/actions/printInstructionsActions';

import SnapToEdge from './tools/snap-to-edge/SnapToEdge';
import LockArtworkRatio from './tools/lock-artwork-ratio/LockArtworkRatio';
import ZoomTool from '@components/zoom-tool/ZoomTool';
import ExpandableInput from '@components/expandable-input/ExpandableInput';
import './TopToolPanel.scss';
import { isCreatedFromDielineShape } from '@app/utils/hints';

const TopToolPanel = ({
  editorZoom,
  editor,
  snapToEdgeVisible,
  lockArtworkRatioEnabled,
  printInstructionsNeeded,
  isPrintInstructionsReadOnly,
  printInstructions,
  printInstructionsExpanded,
  setPrintInstructions,
  setPrintInstructionsExpanded,
}) => {
  const { minZoom = 0.05, maxZoom = 5 } = editor || {};
  const setZoomLevel = zoomValue => editor?.setZoomLevel(zoomValue);
  const scrollToCenter = () => editor?.scrollToCenter();
  const autoZoom = () => editor?.autoZoom();

  return (
    <div id="qau-top-tool-panel" className={classnames({ 'with-print-instructions': printInstructionsNeeded })}>
      {printInstructionsNeeded && (
        <ExpandableInput
          titleLocKey="qau-print-instructions-title"
          placeholderLocKey="qau-print-instructions-placeholder"
          collapseLocKey="qau-print-instructions-done-btn"
          value={printInstructions}
          expanded={printInstructionsExpanded}
          readOnly={isPrintInstructionsReadOnly}
          onValueChange={setPrintInstructions}
          onExpandedChange={setPrintInstructionsExpanded}
        />
      )}
      <div className="grouped-tools">
        {snapToEdgeVisible && <SnapToEdge />}
        {lockArtworkRatioEnabled && <LockArtworkRatio />}
      </div>
      <ZoomTool
        currentZoom={editorZoom}
        minZoom={minZoom}
        maxZoom={maxZoom}
        setZoomLevel={setZoomLevel}
        scrollToCenter={scrollToCenter}
        autoZoom={autoZoom}
      />
    </div>
  );
};

const mapStateToProps = (
  {
    customize: {
      customizeData: { editor, editorZoom, snapToEdgeVisible },
    },
    printInstructions: { printInstructions, expanded: printInstructionsExpanded },
    matrix,
  },
  { flowConfig, context },
) => {
  const { quote = {} } = context.projectData || {};
  const { specialRequest } = quote || {};
  const isMobileView = isMobileMode(matrix.ctx);

  return {
    editor,
    editorZoom,
    snapToEdgeVisible,
    lockArtworkRatioEnabled: !isCustomShapeFlow(flowConfig) && !isCreatedFromDielineShape(context),
    printInstructions,
    printInstructionsExpanded,
    printInstructionsNeeded: !isMobileView && specialRequest === 'Y',
    isPrintInstructionsReadOnly: isPrintInstructionsReadOnly(flowConfig),
  };
};

const mapDispatchToProps = {
  setPrintInstructions,
  setPrintInstructionsExpanded,
};

TopToolPanel.propTypes = {
  editor: PropTypes.object,
  editorZoom: PropTypes.number.isRequired,
  snapToEdgeVisible: PropTypes.bool.isRequired,
  lockArtworkRatioEnabled: PropTypes.bool.isRequired,
  printInstructionsNeeded: PropTypes.bool.isRequired,
  isPrintInstructionsReadOnly: PropTypes.bool.isRequired,
  printInstructions: PropTypes.string,
  printInstructionsExpanded: PropTypes.bool.isRequired,
  setPrintInstructions: PropTypes.func.isRequired,
  setPrintInstructionsExpanded: PropTypes.func.isRequired,
};

export default inject('flowConfig', 'context')(connect(mapStateToProps, mapDispatchToProps)(TopToolPanel));
